import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drag-resizable filter-rail width, persisted per module.
 *
 * The third of the layout hooks, and it follows `useColumnWidths` and `useRowHeight`
 * exactly: same storage discipline, same clamp-on-read, same "the drag runs to the space on
 * offer" ceiling. A facet's values are unit names and position titles, and at a fixed 288px
 * every one of them ellipsized — the reader could see there were filters but not what they
 * were.
 *
 * One width for the rail, not per facet: the groups are columns of one list, and a rail whose
 * sections were different widths would lose the left edge the eye scans down.
 */

/** Narrower than this and a checkbox row has no room for a label beside its count. */
const MIN_WIDTH = 200;

/**
 * The floor under the drag ceiling, not the ceiling itself — half the viewport is that. It
 * only keeps the behaviour sane in a very narrow window, where half of it is already less
 * than a usable rail.
 */
const MIN_MAX_WIDTH = 320;

/** `w-72`, which is what the rail was before it could be dragged. */
export const DEFAULT_RAIL_WIDTH = 288;

function storageKey(key: string): string {
  return `index.${key}.railWidth`;
}

export function useRailWidth(key: string) {
  const [width, setWidth] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(storageKey(key));

      if (stored !== null) {
        const parsed = Number(stored);

        // Clamped on read: a stored value from an older floor must not survive as one.
        if (Number.isFinite(parsed)) {
          return Math.max(MIN_WIDTH, parsed);
        }
      }
    } catch {
      // Blocked or unparsable storage — fall through to the default.
    }

    return DEFAULT_RAIL_WIDTH;
  });

  /*
    Exposed so the rail can drop its open/close width transition while a drag is running.
    Without that the rail eases toward the pointer over 200ms instead of tracking it, which
    reads as lag rather than as animation.
  */
  const [resizing, setResizing] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(key), String(width));
    } catch {
      // Persisting the layout is a convenience, never a requirement.
    }
  }, [key, width]);

  const drag = useRef<{ startX: number; startWidth: number; maxWidth: number } | null>(null);

  const onPointerMove = useCallback((event: PointerEvent) => {
    const state = drag.current;
    if (!state) return;

    setWidth(
      Math.min(state.maxWidth, Math.max(MIN_WIDTH, state.startWidth + (event.clientX - state.startX))),
    );
  }, []);

  const onPointerUp = useCallback(() => {
    if (!drag.current) return;

    drag.current = null;
    setResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  const startResize = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();

      /*
        Seeded from the MEASURED rail rather than the stored number, the same reason a column
        and a row are: the two can differ — a clamp on read, a viewport that shrank — and
        seeding from storage makes the rail jump on the first pixel instead of tracking the
        pointer.
      */
      const rail = (event.currentTarget as HTMLElement).parentElement;
      const measured = rail?.getBoundingClientRect().width;

      /*
        Half the window: the rail narrows the list it filters, so a rail that can be dragged
        over most of the screen is one that can hide what it is for.
      */
      drag.current = {
        startX: event.clientX,
        startWidth: Math.round(measured || width),
        maxWidth: Math.max(MIN_MAX_WIDTH, Math.round(window.innerWidth / 2)),
      };

      setResizing(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width],
  );

  const reset = useCallback(() => setWidth(DEFAULT_RAIL_WIDTH), []);

  return { width, resizing, startResize, reset };
}
